import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "app/store";
import { useTeamManagement } from "./useTeamManagement";
import { Team } from "models/schemas";
import { useTeamName } from "pages/team_settings/hooks";

/**
 * Hook to handle team switching when an object from a different team is loaded
 * @param teamName The data source team name
 */
export const useCheckTeamSwitch = (teamName: string) => {
  const { currentResourceTeam, headerSelectedTeam } = useTeamName();
  const allTeams: any = useAppSelector((state) => state.teamSettings?.allTeams);

  const {
    updateSelectedTeam,
    updateHeaderSelectedTeam,
    updateUserCriticalnessInTeam,
    updateTeamDataAndSchemas,
  } = useTeamManagement();

  useEffect(() => {
    if (!teamName || !allTeams?.teams) {
      return;
    }

    const isTeamMember = allTeams.teams.some(
      (team: Team) => team.teamId === teamName
    );

    if (isTeamMember) {
      const teamDescription =
        allTeams.teams.find((team: Team) => team.teamId === teamName)
          ?.teamDescription || "";

      // Update header and data team separately
      if (teamName !== headerSelectedTeam) {
        updateHeaderSelectedTeam(teamName, teamDescription);
      }

      if (teamName !== currentResourceTeam) {
        updateSelectedTeam(teamName);

        // Only update team-specific data when data team changes
        const userCriticalMap: { [key: string]: boolean } = {};
        const teamSettingsMap = new Map<string, Team>();

        allTeams.teams.forEach((team: Team) => {
          userCriticalMap[team.teamId] = team.isCritical;
          teamSettingsMap.set(team.teamId, team);
        });

        updateUserCriticalnessInTeam(teamName, userCriticalMap);
        updateTeamDataAndSchemas(teamName, teamSettingsMap);
      }
    } else {
      // Guest logic
      if (headerSelectedTeam !== "guest") {
        updateHeaderSelectedTeam("guest", "Guest");
      }
      if (currentResourceTeam !== teamName) {
        updateSelectedTeam(teamName);
      }
    }
  }, [
    teamName,
    headerSelectedTeam,
    currentResourceTeam,
    allTeams,
    updateSelectedTeam,
    updateHeaderSelectedTeam,
    updateUserCriticalnessInTeam,
    updateTeamDataAndSchemas,
  ]);
};
